import type { EvidenceType } from '@oip/contracts';

/**
 * Evidence presentation constants.
 *
 * Restated rather than imported: `@oip/contracts` compiles to CommonJS so the
 * web app can take its *types* but not its runtime values (risk R9), and the
 * content-type allow-list lives in `@oip/database`, which the browser must not
 * import at all. The `satisfies` clauses below tie these back to the contract
 * types, so a renamed or removed member fails the build rather than drifting
 * silently — the same arrangement `outcomeOptions.ts` uses.
 */

export const EVIDENCE_TYPE_OPTIONS = [
  { value: 'PHOTO', label: 'Photo' },
  { value: 'MAINTENANCE_INVOICE', label: 'Maintenance invoice' },
  { value: 'WORK_ORDER', label: 'Work order' },
  { value: 'INSPECTION_REPORT', label: 'Inspection report' },
  { value: 'PDF_DOCUMENT', label: 'PDF document' },
  { value: 'DASHCAM_VIDEO', label: 'Dashcam video' },
  { value: 'CAN_DIAGNOSTIC', label: 'CAN diagnostic' },
  { value: 'OTHER', label: 'Other' },
] satisfies ReadonlyArray<{ value: EvidenceType; label: string }>;

/**
 * MIME prefixes the API accepts per evidence type — a MIRROR of
 * `EVIDENCE_CONTENT_TYPE_PREFIXES` in `@oip/database`, which is the authority.
 *
 * Used for two things, neither of which is enforcement: the file picker's
 * `accept` attribute, and refusing an obviously wrong file before spending an
 * upload round trip on a 400. The server re-checks regardless.
 */
export const EVIDENCE_ACCEPT: Record<EvidenceType, string[]> = {
  PHOTO: ['image/'],
  MAINTENANCE_INVOICE: ['application/pdf', 'image/'],
  WORK_ORDER: ['application/pdf', 'image/'],
  INSPECTION_REPORT: ['application/pdf', 'image/'],
  PDF_DOCUMENT: ['application/pdf'],
  DASHCAM_VIDEO: ['video/'],
  CAN_DIAGNOSTIC: ['application/pdf', 'text/', 'application/json', 'application/octet-stream'],
  OTHER: [
    'application/pdf',
    'image/',
    'video/',
    'text/',
    'application/json',
    'application/octet-stream',
  ],
};

/** Prefix match, mirroring `contentTypeAllowed` server-side. */
export function contentTypeAllowed(evidenceType: EvidenceType, contentType: string): boolean {
  return EVIDENCE_ACCEPT[evidenceType].some((p) => contentType.startsWith(p));
}

/**
 * `accept` for an `<input type="file">`.
 *
 * A bare prefix such as `image/` is not valid there — the attribute wants
 * `image/*` — so trailing-slash prefixes are expanded and exact types passed
 * through unchanged.
 */
export function acceptAttribute(evidenceType: EvidenceType): string {
  return EVIDENCE_ACCEPT[evidenceType].map((p) => (p.endsWith('/') ? `${p}*` : p)).join(',');
}

export const EVIDENCE_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Upload incomplete',
  AVAILABLE: 'Available',
  QUARANTINED: 'Quarantined',
  DELETED: 'Deleted',
};

/** Bytes to a short human string. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/**
 * Hex SHA-256 of a file, computed in the browser.
 *
 * The API requires the digest up front, before any bytes are uploaded. Note
 * that `crypto.subtle` is only present in a secure context — over plain HTTP on
 * a non-localhost host it is undefined, which is a deployment constraint worth
 * failing loudly on rather than silently sending a wrong digest.
 */
export async function sha256Hex(file: Blob): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      'Secure context required: file checksums need window.crypto.subtle, which browsers expose only over HTTPS or on localhost.',
    );
  }
  const buffer = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
